import type { Schemas } from "@10twenty/api";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { api, publicApi, unwrap } from "../api";
import { describeBrowser, webDevice } from "./device";

export const passkeysSupported = browserSupportsWebAuthn;

/** Usernameless passkey sign-in; creates a new device session. */
export async function signInWithPasskey(): Promise<Schemas["AuthSession"]> {
  const options = unwrap(await publicApi.POST("/auth/passkeys/login/options"));
  const credential = await startAuthentication({
    optionsJSON: options.public_key as unknown as PublicKeyCredentialRequestOptionsJSON,
  });
  return unwrap(
    await publicApi.POST("/auth/passkeys/login/verify", {
      body: {
        challenge_id: options.challenge_id,
        credential: credential as unknown as Record<string, unknown>,
        device: webDevice(),
      },
    }),
  );
}

/** Registers a passkey on this browser for the signed-in user. */
export async function registerPasskey(): Promise<Schemas["Passkey"]> {
  const options = unwrap(await api.POST("/auth/passkeys/registration/options"));
  const credential = await startRegistration({
    optionsJSON: options.public_key as unknown as PublicKeyCredentialCreationOptionsJSON,
  });
  return unwrap(
    await api.POST("/auth/passkeys/registration/verify", {
      body: {
        challenge_id: options.challenge_id,
        name: describeBrowser(navigator.userAgent),
        credential: credential as unknown as Record<string, unknown>,
      },
    }),
  );
}

/** The user dismissed or timed out the browser's passkey prompt. */
export function isPasskeyCancelled(error: unknown): boolean {
  return error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError");
}
